import { useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { motion, useScroll, useSpring } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Download, Languages, Linkedin, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { CATEGORIES } from "@/lib/observatoire";
import { BookingButton } from "@/components/BookingButton";
import { Reveal, Overline } from "@/components/Reveal";
import { useSEO } from "@/lib/useSEO";
import { NEWSLETTERS, LABELS, getIssue, issuePath, readingTime } from "@/lib/newsletters";

function ReadingProgress() {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 30 });
  return <motion.div data-testid="reading-progress-bar" style={{ scaleX }} className="fixed top-0 left-0 right-0 h-[3px] bg-[#C5A059] origin-left z-[60]" />;
}

function Block({ b, i, L }) {
  if (b.h2)
    return <h2 className="mt-16 mb-6 font-serif-display text-2xl lg:text-3xl font-light text-[#C5A059] leading-snug">{b.h2}</h2>;
  if (b.list)
    return (
      <ol className="my-8 space-y-5 border-l border-[#C5A059] pl-6">
        {b.list.map(([k, v], j) => (
          <li key={j} className="text-[#0A0A0A]/80 font-light leading-relaxed">
            <span className="font-medium text-[#0A0A0A]">{k}{L.colon}</span>{v}
          </li>
        ))}
      </ol>
    );
  if (b.question)
    return (
      <blockquote data-testid="article-question" className="my-10 bg-[#0A0A0A] p-8 lg:p-10 font-serif-display italic text-xl lg:text-2xl text-[#F9F6F0] leading-snug border-l-2 border-[#C5A059]">
        {b.question}
      </blockquote>
    );
  return <p key={i} className="mb-6 text-[#0A0A0A]/80 text-base lg:text-lg font-light leading-relaxed">{b.p}</p>;
}

function AuthorBox({ n, L }) {
  return (
    <Reveal testId="article-author-box" className="mt-16 bg-[#0A0A0A] p-8 lg:p-10 flex flex-col sm:flex-row gap-8 border-t-2 border-[#C5A059]">
      <img src={n.authorPhoto} alt={n.author} className="w-28 h-36 object-cover object-top shrink-0" />
      <div>
        <span className="text-xs uppercase tracking-[0.25em] font-semibold text-[#C5A059]">{L.about}</span>
        <h3 className="mt-4 font-serif-display text-2xl text-[#F9F6F0]">{n.author}</h3>
        <p className="mt-1 text-xs uppercase tracking-[0.2em] text-[#C5A059]">{n.role}</p>
        <a href={n.linkedin} target="_blank" rel="noopener noreferrer" data-testid="article-author-linkedin" className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#F9F6F0]/70 hover:text-[#C5A059] transition-colors">
          <Linkedin size={15} /> {L.follow}
        </a>
        <p data-testid="article-author-bio" className="mt-5 text-[#F9F6F0]/70 font-light leading-relaxed">{n.bio}</p>
        <div className="mt-8 flex flex-wrap gap-4">
        <Link
          to={`/contact?message=${encodeURIComponent(L.contactMsg(n))}`}
          data-testid="article-author-contact-btn"
          className="group inline-flex items-center gap-2 bg-[#C5A059] text-[#0A0A0A] text-xs uppercase tracking-[0.2em] px-6 py-4 hover:bg-[#F9F6F0] transition-colors duration-500"
        >
          {L.contact} <ArrowUpRight size={16} className="transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
        </Link>
        <BookingButton label={L.book} testId="article-booking-btn" />
        </div>
      </div>
    </Reveal>
  );
}

function PdfButton({ n, L }) {
  const [busy, setBusy] = useState(false);
  const onClick = async () => {
    setBusy(true);
    try {
      const { downloadNewsletterPdf } = await import("@/lib/newsletterPdf");
      await downloadNewsletterPdf(n, L);
    } catch {
      toast.error(L.pdfError);
    } finally {
      setBusy(false);
    }
  };
  return (
    <button type="button" onClick={onClick} disabled={busy} data-testid="article-download-pdf" className="group inline-flex items-center gap-2 border border-[#C5A059] text-[#C5A059] text-xs uppercase tracking-[0.2em] px-6 py-4 hover:bg-[#C5A059] hover:text-[#0A0A0A] transition-colors duration-500 disabled:opacity-60">
      {busy ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />} {L.pdf}
    </button>
  );
}

function RelatedIssues({ n, L }) {
  const issues = NEWSLETTERS.map((x) => getIssue(x.slug, n.lang)).filter((x) => x && x.slug !== n.slug && x.category === n.category);
  const upcoming = n.lang === "fr" ? CATEGORIES.find((c) => c.name === n.category)?.items || [] : [];
  if (!issues.length && !upcoming.length) return null;
  return (
    <section data-testid="related-issues" className="bg-[#0A0A0A] py-20">
      <div className="mx-auto max-w-[1600px] px-6 lg:px-12">
        <Overline>{L.related} · {n.category}</Overline>
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {issues.map((x) => (
            <Link key={x.slug} to={issuePath(x.slug, x.lang)} data-testid={`related-issue-${x.slug}`} className="group border border-white/10 hover:border-[#C5A059]/70 transition-colors duration-500">
              <img src={x.cover} alt={x.title} className="w-full aspect-video object-cover" />
              <div className="p-8">
                <span className="text-xs uppercase tracking-[0.2em] text-[#C5A059]">{x.number} · {x.date}</span>
                <h3 className="mt-4 font-serif-display text-xl text-[#F9F6F0] leading-snug group-hover:text-[#C5A059] transition-colors">{x.title}</h3>
              </div>
            </Link>
          ))}
          {upcoming.map((u) => (
            <div key={u.ref} data-testid={`related-upcoming-${u.ref.replace(/\D/g, "")}`} className="border border-white/10 p-8 flex flex-col">
              <div className="flex justify-between text-xs uppercase tracking-[0.2em]">
                <span className="text-[#C5A059]">{u.type}</span>
                <span className="text-[#F9F6F0]/40">{u.date}</span>
              </div>
              <h3 className="mt-6 font-serif-display text-xl text-[#F9F6F0] leading-snug">{u.t}</h3>
              <p className="mt-3 text-[#F9F6F0]/60 font-light text-sm leading-relaxed flex-1">{u.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function NewsletterArticle({ lang = "fr" }) {
  const { slug } = useParams();
  const n = getIssue(slug, lang);
  const L = LABELS[lang];
  useSEO({
    fullTitle: n ? `${n.title} | ${L.back}` : undefined,
    title: n?.title,
    description: n?.excerpt,
    path: issuePath(slug, lang),
  });
  if (!n) return <Navigate to="/observatoire" replace />;
  const shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(`https://herusaxiom.com${issuePath(n.slug, lang)}`)}`;

  return (
    <article data-testid="newsletter-article" lang={lang} className="bg-[#F9F6F0]">
      <ReadingProgress />
      <header className="bg-[#0A0A0A] pt-36 lg:pt-44">
        <div className="mx-auto max-w-4xl px-6 lg:px-12 pb-14">
          <Link to="/observatoire" data-testid="article-back-link" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#F9F6F0]/60 hover:text-[#C5A059] transition-colors">
            <ArrowLeft size={14} /> {L.back}
          </Link>
          <div className="mt-10"><Overline>{L.series} — {n.number}</Overline></div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.19, 1, 0.22, 1] }}
            data-testid="article-title"
            className="mt-8 font-serif-display font-light leading-[1.05] tracking-tight text-[#F9F6F0] text-4xl sm:text-5xl lg:text-6xl"
          >
            {n.title}
          </motion.h1>
          <div data-testid="article-meta" className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-xs uppercase tracking-[0.2em] text-[#F9F6F0]/50">
            <span className="text-[#C5A059]">{n.category}</span>
            <span>{n.date}</span>
            <span data-testid="article-reading-time">{readingTime(n)} {L.read}</span>
          </div>
          <div data-testid="article-author" className="mt-6 border-l border-[#C5A059] pl-4">
            <p className="font-serif-display text-lg text-[#F9F6F0]">{L.by(n.author)}</p>
            <p className="text-xs uppercase tracking-[0.2em] text-[#C5A059]">{n.role}</p>
          </div>
          <div className="mt-8 flex flex-wrap gap-4">
            <PdfButton n={n} L={L} />
            {getIssue(n.slug, L.otherLang) && (
              <Link to={issuePath(n.slug, L.otherLang)} data-testid="article-language-switch" className="inline-flex items-center gap-2 border border-[#F9F6F0]/20 text-[#F9F6F0]/80 text-xs uppercase tracking-[0.2em] px-6 py-4 hover:border-[#C5A059] hover:text-[#C5A059] transition-colors">
                <Languages size={16} /> {L.other}
              </Link>
            )}
          </div>
        </div>
        <div className="mx-auto max-w-6xl px-6 lg:px-12">
          <img data-testid="article-cover" src={n.cover} alt={n.title} className="w-full h-auto translate-y-16 shadow-2xl" />
        </div>
      </header>

      <div className="mx-auto max-w-3xl px-6 lg:px-0 pt-32 pb-20">
        <Reveal>
          <p data-testid="article-lead" className="mb-12 font-serif-display text-xl lg:text-2xl text-[#0A0A0A] leading-relaxed">{n.lead}</p>
        </Reveal>
        {n.body.map((b, i) => <Block key={i} b={b} i={i} L={L} />)}

        <div className="mt-16 border-t border-[#0A0A0A]/10 pt-10">
          <p className="italic text-[#0A0A0A]/70 font-light leading-relaxed">{n.outro}</p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/observatoire#abonnement" data-testid="article-subscribe-link" className="group inline-flex items-center gap-2 bg-[#0A0A0A] text-[#F9F6F0] text-xs uppercase tracking-[0.2em] px-6 py-4 hover:bg-[#C5A059] hover:text-[#0A0A0A] transition-colors duration-500">
              {L.subscribe} <ArrowUpRight size={16} />
            </Link>
            <Link to="/observatoire/newsletter" data-testid="article-archive-link" className="inline-flex items-center gap-2 border border-[#0A0A0A]/20 text-[#0A0A0A] text-xs uppercase tracking-[0.2em] px-6 py-4 hover:border-[#C5A059] hover:text-[#C5A059] transition-colors">
              {L.all}
            </Link>
            <a href={shareUrl} target="_blank" rel="noopener noreferrer" data-testid="article-share-linkedin" className="inline-flex items-center gap-2 border border-[#0A0A0A]/20 text-[#0A0A0A] text-xs uppercase tracking-[0.2em] px-6 py-4 hover:border-[#C5A059] hover:text-[#C5A059] transition-colors">
              <Linkedin size={16} /> {L.share}
            </a>
          </div>
        </div>

        <section data-testid="article-sources" className="mt-16">
          <h3 className="text-xs uppercase tracking-[0.25em] font-semibold text-[#C5A059]">{n.sourcesTitle || L.sources}</h3>
          <ul className="mt-6 space-y-3 text-sm text-[#0A0A0A]/70 font-light leading-relaxed">
            {n.sources.map(([s, d], j) => <li key={j}><span className="text-[#0A0A0A]">{s}</span>{d && `${L.colon}${d}`}</li>)}
          </ul>
          <p className="mt-6 text-xs text-[#0A0A0A]/50 italic">{n.disclaimer}</p>
        </section>

        <AuthorBox n={n} L={L} />
      </div>
      <RelatedIssues n={n} L={L} />
    </article>
  );
}
